import type { FC } from 'react';
import React, { useContext } from 'react';
import ReactDOM from 'react-dom';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Box, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { expValEquals } from '@confluence/feature-experiments';

const PDF_BANNER_PORTAL_ID = 'pdf-export-banner-root';

const i18n = defineMessages({
	bannerMessage: {
		id: 'pdf-export.banner.message',
		defaultMessage:
			"We've received your print request. To maintain your page's format, we're exporting it to a PDF for you to download and print.",
		description: 'Message displayed in the banner when PDF export begins',
	},
});

const styles = cssMap({
	bannerStyles: {
		padding: token('space.200'),
		backgroundColor: token('color.background.neutral.bold'),
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const PdfBanner: FC = () => (
	<Box xcss={styles.bannerStyles}>
		<Text color="color.text.inverse">
			<FormattedMessage {...i18n.bannerMessage} />
		</Text>
	</Box>
);

export const PdfBannerPortal: FC = () => {
	const { getQueryParams } = useContext(RoutesContext);

	const queryParams = getQueryParams();

	if (!expValEquals('afm-enable-command-p-reroute', 'isEnabled', true)) {
		return null;
	}

	// only inject if ?showPdfBanner=true
	if (queryParams.showPdfBanner !== 'true') {
		return null;
	}
	let targetElement = document.getElementById(PDF_BANNER_PORTAL_ID);

	// if the target doesn't exist, create and insert it before <main>
	if (!targetElement) {
		const main = document.querySelector('main#main');
		if (main && main.parentElement) {
			targetElement = document.createElement('div');
			targetElement.id = PDF_BANNER_PORTAL_ID;
			main.parentElement.insertBefore(targetElement, main);
		}
	}

	// if no anchor could be found, bail
	if (!targetElement) {
		return null;
	}

	return ReactDOM.createPortal(<PdfBanner />, targetElement);
};
